import { SCROLL_AXES, type ScrollAxis, type ScrollAxisName } from "./scroll-box-axes"
import { countWholeUnitsThatFit } from "./scroll-box-capacity"
import { findRepeatingItems, findTextLines, itemContentUnits, type ContentUnit } from "./scroll-box-content"
import { measureEffectiveSize } from "./scroll-box-effective-size"
import { largerMiddleMedian } from "./scroll-box-median"
import { hidesOwnContentAlong, lineHeightPx } from "./scroll-box-styles"

export const MINIMUM_WHOLE_UNITS_IN_A_SCROLL_BOX = 3

export type ScrollBoxUnitKind = "items" | "lines" | "line-heights"

export interface AxisVerdict {
  axis: ScrollAxisName
  unitKind: ScrollBoxUnitKind
  unitsInContent: number
  wholeUnitsThatFit: number
  wholeUnitsRequired: number
  medianUnitPx: number
  effectivePx: number
  overflowPx: number
  stickyStartPx: number
  stickyEndPx: number
  isTooSmall: boolean
}

export interface ScrollBoxVerdict {
  isTooSmall: boolean
  overflowingAxes: AxisVerdict[]
}

interface UnitMeasurement {
  unitKind: ScrollBoxUnitKind
  unitsInContent: number
  wholeUnitsThatFit: number
  wholeUnitsRequired: number
  medianUnitPx: number
}

function measureCountableUnits(
  unitKind: ScrollBoxUnitKind,
  units: ContentUnit[],
  axis: ScrollAxis,
  effectivePx: number,
  minimumWholeUnits: number,
): UnitMeasurement {
  return {
    unitKind,
    unitsInContent: units.length,
    wholeUnitsThatFit: countWholeUnitsThatFit(units, axis, effectivePx),
    wholeUnitsRequired: Math.min(minimumWholeUnits, units.length),
    medianUnitPx: largerMiddleMedian(units.map((unit) => unit.endPx - unit.startPx)),
  }
}

function measureLineHeights(box: Element, effectivePx: number, minimumWholeUnits: number): UnitMeasurement {
  const boxLineHeightPx = lineHeightPx(box)
  return {
    unitKind: "line-heights",
    unitsInContent: 0,
    wholeUnitsThatFit: Math.floor(effectivePx / boxLineHeightPx),
    wholeUnitsRequired: minimumWholeUnits,
    medianUnitPx: boxLineHeightPx,
  }
}

function textLinesStackAlong(axis: ScrollAxis): boolean {
  return axis.name === "vertical"
}

function measureUnits(box: Element, axis: ScrollAxis, effectivePx: number, minimumWholeUnits: number): UnitMeasurement {
  const items = findRepeatingItems(box, axis)
  if (items.length > 0) {
    return measureCountableUnits("items", itemContentUnits(items, axis), axis, effectivePx, minimumWholeUnits)
  }
  const lines = textLinesStackAlong(axis) ? findTextLines(box, axis, effectivePx) : []
  if (lines.length > 0) return measureCountableUnits("lines", lines, axis, effectivePx, minimumWholeUnits)
  return measureLineHeights(box, effectivePx, minimumWholeUnits)
}

function judgeAxis(box: Element, axis: ScrollAxis, minimumWholeUnits: number): AxisVerdict {
  const size = measureEffectiveSize(box, axis)
  const units = measureUnits(box, axis, size.effectivePx, minimumWholeUnits)
  return {
    axis: axis.name,
    ...units,
    effectivePx: size.effectivePx,
    overflowPx: size.overflowPx,
    stickyStartPx: size.stickyStartPx,
    stickyEndPx: size.stickyEndPx,
    isTooSmall: units.wholeUnitsThatFit < units.wholeUnitsRequired,
  }
}

export function judgeScrollBox(box: Element, minimumWholeUnits: number): ScrollBoxVerdict {
  const overflowingAxes = SCROLL_AXES.filter((axis) => hidesOwnContentAlong(box, axis)).map((axis) =>
    judgeAxis(box, axis, minimumWholeUnits),
  )
  return { isTooSmall: overflowingAxes.some((verdict) => verdict.isTooSmall), overflowingAxes }
}
